import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import { useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import {
  CHAPTERS,
  MODULE_BRIEFS,
  PHASES,
  ROLE_BRIEFS,
  type GuideChapter,
  type GuideProcedure,
} from '../documentation/guide'
import { useAdminContext } from '../session'

interface AccessCatalogue {
  permissions: { code: string; module: string; description: string; is_sensitive: boolean }[]
  roles: {
    code: string
    name_i18n: Record<string, string>
    description_i18n: Record<string, string>
    is_system: boolean
    permissions: string[]
  }[]
  my_roles: { id: string; code: string; event_id: string | null }[]
  mine: string[]
}

/**
 * Le manuel, dans l'application.
 *
 * Un document séparé se périme : il vit dans un dossier partagé, personne ne
 * l'ouvre le soir venu, et la version qui circule n'est jamais la dernière.
 * Celui-ci est à un clic de l'écran qu'il explique.
 *
 * **Deux sources, et c'est le choix qui compte.** Le déroulé — qui fait quoi,
 * dans quel ordre — est écrit dans le code, parce qu'il doit changer en même
 * temps que les écrans qu'il décrit. Les permissions et la composition des
 * rôles, elles, sont lues **en direct** : les recopier ici les aurait fait
 * dériver au premier ajout, et un manuel qui ment sur les droits est pire
 * qu'un manuel absent.
 *
 * La page va plus loin que rendre les deux côte à côte : elle les **confronte**.
 * Un rôle présent en base qu'aucune fiche ne décrit est signalé comme tel,
 * plutôt que de disparaître silencieusement de la documentation.
 *
 * Une seule langue, le français. Écrire un manuel d'exploitation en trois
 * langues est un travail de traduction, pas de code, et trois versions à
 * moitié faites valent moins qu'une seule qui dit vrai — la page le dit
 * plutôt que de laisser croire à un oubli.
 */
export function AdminDocumentationPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const [phase, setPhase] = useState<'tout' | GuideChapter['phase']>('tout')
  const [query, setQuery] = useState('')
  const [openChapter, setOpenChapter] = useState<string | null>(CHAPTERS[0]?.id ?? null)

  useEffect(() => {
    document.title = t('admin.section.documentation')
  }, [t])

  const access = useAdminQuery<AccessCatalogue>(
    ['access-catalogue', eventId],
    '/auth/access-catalogue',
    eventId,
  )

  const needle = query.trim().toLowerCase()

  const chapters = useMemo(
    () =>
      CHAPTERS.filter((chapter) => {
        if (phase !== 'tout' && chapter.phase !== phase) return false
        if (!needle) return true
        const haystack = [
          chapter.title,
          chapter.intro,
          ...(chapter.notes ?? []).flatMap((note) => [note.title, note.body]),
          ...(chapter.procedures ?? []).flatMap((step) => [
            step.title,
            step.who,
            step.where,
            step.needs ?? '',
            step.warning ?? '',
            step.why ?? '',
            ...step.steps,
          ]),
        ]
          .join(' ')
          .toLowerCase()
        return haystack.includes(needle)
      }),
    [phase, needle],
  )

  // Une recherche ne sert à rien si elle laisse les résultats repliés.
  const forceOpen = needle.length > 0

  return (
    <>
      <AdminHeader
        title={t('admin.section.documentation')}
        description={t('admin.docs.intro')}
      />

      <div className="mb-6 grid gap-3">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('admin.docs.searchPlaceholder')}
          aria-label={t('admin.docs.search')}
          className="w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary sm:max-w-md"
        />

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={phase === 'tout' ? 'champagne' : 'outline'}
            onClick={() => setPhase('tout')}
          >
            {t('admin.docs.all')}
          </Button>
          {PHASES.map((entry) => (
            <Button
              key={entry.id}
              size="sm"
              variant={phase === entry.id ? 'champagne' : 'outline'}
              onClick={() => setPhase(entry.id)}
            >
              {entry.label}
            </Button>
          ))}
        </div>

        {phase !== 'tout' ? (
          <p className="text-caption text-content-muted">
            {PHASES.find((entry) => entry.id === phase)?.hint}
          </p>
        ) : null}
      </div>

      {chapters.length === 0 ? (
        <p className="text-sm text-content-secondary">{t('admin.docs.noMatch')}</p>
      ) : null}

      <div className="grid gap-2">
        {chapters.map((chapter) => (
          <ChapterCard
            key={chapter.id}
            chapter={chapter}
            open={forceOpen || openChapter === chapter.id}
            onToggle={() =>
              setOpenChapter(openChapter === chapter.id ? null : chapter.id)
            }
          />
        ))}
      </div>

      {/* ── Les postes et leurs droits ──────────────────────────── */}
      {phase === 'tout' || phase === 'comprendre' ? (
        <>
          <TeamSection
            catalogue={access.data}
            locale={i18n.language}
            pending={access.isPending}
          />
          <PermissionSection catalogue={access.data} pending={access.isPending} />
        </>
      ) : null}

      <p className="mt-10 text-caption text-content-muted">{t('admin.docs.language')}</p>
    </>
  )
}

/** Un chapitre : son intro, ses paragraphes, ses procédures. */
function ChapterCard({
  chapter,
  open,
  onToggle,
}: {
  chapter: GuideChapter
  open: boolean
  onToggle: () => void
}) {
  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-4 text-left sm:px-5"
      >
        <span aria-hidden className="shrink-0 text-xl">
          {chapter.icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-heading-sm text-content-primary">
            {chapter.title}
          </span>
          <span className="mt-0.5 block text-sm text-content-secondary">
            {chapter.intro}
          </span>
        </span>
        <span aria-hidden className="shrink-0 text-content-muted">
          {open ? '−' : '+'}
        </span>
      </button>

      {open ? (
        <div className="border-t border-line-subtle px-4 py-5 sm:px-5">
          {chapter.notes?.map((note) => (
            <section key={note.title} className="mb-5 last:mb-0">
              <h3 className="text-sm font-semibold text-content-primary">{note.title}</h3>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-content-secondary">
                {note.body}
              </p>
            </section>
          ))}

          {chapter.procedures?.map((procedure) => (
            <ProcedureCard key={procedure.id} procedure={procedure} />
          ))}
        </div>
      ) : null}
    </Card>
  )
}

/**
 * Une procédure.
 *
 * Toujours le même ordre : qui, où, avec quoi, puis les gestes numérotés. Un
 * mode d'emploi qui commence par les gestes oblige à lire jusqu'au bout pour
 * découvrir qu'on n'est pas la bonne personne.
 */
function ProcedureCard({ procedure }: { procedure: GuideProcedure }) {
  const { t } = useTranslation()

  return (
    <section className="mb-4 rounded-lg border border-line-subtle bg-bg-base/40 p-4 last:mb-0">
      <h3 className="font-display text-heading-sm text-content-primary">
        {procedure.title}
      </h3>

      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-3">
        <Meta label={t('admin.docs.who')} value={procedure.who} />
        <Meta label={t('admin.docs.where')} value={procedure.where} />
        {procedure.needs ? (
          <Meta label={t('admin.docs.needs')} value={procedure.needs} />
        ) : null}
      </dl>

      <ol className="mt-4 grid list-decimal gap-1.5 pl-5 text-sm leading-relaxed text-content-secondary">
        {procedure.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      {procedure.warning ? (
        <p className="mt-4 rounded-md border border-ember-400/40 bg-ember-400/5 px-3 py-2.5 text-sm text-content-secondary">
          <span className="mr-1 font-semibold text-ember-300">
            {t('admin.docs.watchOut')}
          </span>
          {procedure.warning}
        </p>
      ) : null}

      {procedure.why ? (
        <p className="mt-3 border-l-2 border-line pl-3 text-sm italic text-content-muted">
          <span className="mr-1 font-semibold not-italic">{t('admin.docs.why')}</span>
          {procedure.why}
        </p>
      ) : null}
    </section>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-caption uppercase tracking-wide text-content-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-content-primary">{value}</dd>
    </div>
  )
}

/**
 * Les postes de l'équipe.
 *
 * Chaque fiche décrit une responsabilité — ce que la base ne peut pas dire —
 * et affiche le nombre exact de permissions que le rôle porte réellement, lu
 * en direct. Un rôle qui existe en base sans fiche est signalé : c'est le
 * signe que quelqu'un a créé un rôle sans écrire à quoi il sert.
 */
function TeamSection({
  catalogue,
  locale,
  pending,
}: {
  catalogue: AccessCatalogue | undefined
  locale: string
  pending: boolean
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<string | null>(null)

  const byCode = new Map((catalogue?.roles ?? []).map((role) => [role.code, role]))
  const mine = new Set((catalogue?.my_roles ?? []).map((role) => role.code))

  const undocumented = (catalogue?.roles ?? []).filter(
    (role) => !ROLE_BRIEFS.some((brief) => brief.code === role.code),
  )

  return (
    <section className="mt-10">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.docs.teamTitle')}
      </h2>
      <p className="mt-1 max-w-3xl text-sm text-content-secondary">
        {t('admin.docs.teamIntro')}
      </p>

      {pending ? <Skeleton className="mt-5 h-32 w-full" /> : null}

      <div className="mt-5 grid gap-2">
        {ROLE_BRIEFS.map((brief) => {
          const live = byCode.get(brief.code)
          const expanded = open === brief.code
          return (
            <Card key={brief.code} className="p-0">
              <button
                type="button"
                onClick={() => setOpen(expanded ? null : brief.code)}
                aria-expanded={expanded}
                className="flex w-full flex-col gap-1.5 px-4 py-3.5 text-left sm:flex-row sm:items-center sm:gap-4"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-content-primary">{brief.title}</span>
                  <span className="mt-0.5 block font-mono text-caption text-content-muted">
                    {brief.code}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  {mine.has(brief.code) ? (
                    <Badge tone="champagne">{t('admin.docs.yourRole')}</Badge>
                  ) : null}
                  {/* Le compte de droits vient de la base, jamais de la fiche :
                      un nombre recopié aurait menti dès la permission suivante. */}
                  {live ? (
                    <span className="text-caption tabular-nums text-content-muted">
                      {t('admin.docs.rightsCount', { count: live.permissions.length })}
                    </span>
                  ) : catalogue ? (
                    // Le rôle manque vraiment. Tant que la base n'a pas
                    // répondu, on ne dit rien : afficher « absent » sur toutes
                    // les fiches pendant le chargement ferait paniquer pour
                    // rien.
                    <Badge tone="ember">{t('admin.docs.roleMissing')}</Badge>
                  ) : null}
                  <span className="text-caption text-content-muted">
                    {brief.headcount}
                  </span>
                </span>
              </button>

              {expanded ? (
                <div className="border-t border-line-subtle px-4 py-4">
                  <p className="max-w-3xl text-sm leading-relaxed text-content-secondary">
                    {brief.mission}
                  </p>

                  <p className="mt-3 text-sm text-content-secondary">
                    <span className="text-caption uppercase tracking-wide text-content-muted">
                      {t('admin.docs.screens')}
                    </span>
                    <br />
                    {brief.screens.join(' · ')}
                  </p>

                  <p className="mt-3 max-w-3xl text-sm text-content-secondary">
                    <span className="text-caption uppercase tracking-wide text-content-muted">
                      {t('admin.docs.limits')}
                    </span>
                    <br />
                    {brief.limits}
                  </p>

                  {live ? (
                    <details className="mt-4">
                      <summary className="cursor-pointer text-sm text-content-secondary">
                        {t('admin.docs.showRights', { count: live.permissions.length })}
                      </summary>
                      <ul className="mt-2 grid gap-0.5 sm:grid-cols-2">
                        {live.permissions.map((code) => (
                          <li
                            key={code}
                            className="break-all font-mono text-caption text-content-muted"
                          >
                            {code}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              ) : null}
            </Card>
          )
        })}
      </div>

      {/* Un rôle sans fiche n'est pas caché : c'est le signal qu'on a créé un
          rôle sans écrire à quoi il sert. */}
      {undocumented.length > 0 ? (
        <p className="mt-4 text-sm text-ember-300">
          {t('admin.docs.undocumentedRoles', {
            codes: undocumented
              .map(
                (role) =>
                  role.name_i18n[locale] ||
                  Object.values(role.name_i18n)[0] ||
                  role.code,
              )
              .join(', '),
          })}
        </p>
      ) : null}
    </section>
  )
}

/**
 * La portée de chaque permission, module par module.
 *
 * Entièrement lue en base — c'est le seul moyen qu'elle reste exacte. Ce que
 * le code ajoute, c'est la phrase qui dit à quoi sert chaque module : la base
 * connaît les codes, elle ne sait pas les regrouper en idées.
 */
function PermissionSection({
  catalogue,
  pending,
}: {
  catalogue: AccessCatalogue | undefined
  pending: boolean
}) {
  const { t } = useTranslation()
  const [openModule, setOpenModule] = useState<string | null>(null)
  const [onlyMine, setOnlyMine] = useState(false)

  if (pending) return <Skeleton className="mt-10 h-48 w-full" />
  if (!catalogue) return null

  const mine = new Set(catalogue.mine)
  const rows = onlyMine
    ? catalogue.permissions.filter((row) => mine.has(row.code))
    : catalogue.permissions

  const modules = [...new Set(rows.map((row) => row.module))].sort()

  return (
    <section className="mt-10">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.docs.rightsTitle')}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-content-secondary">
            {t('admin.docs.rightsIntro')}
          </p>
        </div>
        <Button
          size="sm"
          variant={onlyMine ? 'champagne' : 'outline'}
          onClick={() => setOnlyMine(!onlyMine)}
        >
          {t('admin.docs.onlyMine')}
        </Button>
      </div>

      {onlyMine && rows.length === 0 ? (
        <p className="mt-4 text-sm text-content-secondary">{t('admin.docs.noneOfMine')}</p>
      ) : null}

      <div className="mt-5 grid gap-2">
        {modules.map((module) => {
          const inModule = rows.filter((row) => row.module === module)
          const expanded = openModule === module
          return (
            <Card key={module} className="p-0">
              <button
                type="button"
                onClick={() => setOpenModule(expanded ? null : module)}
                aria-expanded={expanded}
                className="flex w-full flex-col gap-1.5 px-4 py-3.5 text-left sm:flex-row sm:items-center sm:gap-4"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-sm text-content-primary">
                    {module}
                  </span>
                  <span className="mt-0.5 block text-caption text-content-secondary">
                    {MODULE_BRIEFS[module] ?? t('admin.docs.moduleUnknown')}
                  </span>
                </span>
                <span className="shrink-0 text-caption tabular-nums text-content-muted">
                  {t('admin.docs.rightsCount', { count: inModule.length })}
                </span>
              </button>

              {expanded ? (
                <ul className="border-t border-line-subtle px-4 py-3">
                  {inModule.map((row) => (
                    <li key={row.code} className="border-b border-line-subtle/60 py-2 last:border-0">
                      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span
                          className={cn(
                            'break-all font-mono text-caption',
                            mine.has(row.code) ? 'text-champagne-300' : 'text-content-muted',
                          )}
                        >
                          {row.code}
                        </span>
                        {row.is_sensitive ? (
                          <Badge tone="ember">{t('admin.docs.sensitive')}</Badge>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block text-sm text-content-secondary">
                        {row.description}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Card>
          )
        })}
      </div>

      <p className="mt-4 text-caption text-content-muted">{t('admin.docs.sensitiveHint')}</p>
    </section>
  )
}
